import { useEffect, useState, useCallback } from "react";
import { api, fmtDate, fmtDT, fmtQty, todayISO } from "../api";
import StatusBadge from "../components/StatusBadge";

const TABS = [
  { id: "inventory", label: "Inventory" },
  { id: "production", label: "Production" },
  { id: "lots", label: "Lots" },
  { id: "movements", label: "Movements" },
  { id: "packing", label: "Packing Lists" },
];

export default function Reports() {
  const [tab, setTab] = useState("inventory");
  const [rows, setRows] = useState(null);
  const [products, setProducts] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [productId, setProductId] = useState("");

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setRows(null);
    try {
      if (tab === "inventory") setRows((await api.get("/inventory")).data);
      else if (tab === "production") setRows((await api.get("/reports/production", { params: { date_from: from, date_to: to, product_id: productId } })).data);
      else if (tab === "lots") setRows((await api.get("/lots", { params: { product_id: productId } })).data);
      else if (tab === "movements") setRows((await api.get("/inventory/movements", { params: { date_from: from, date_to: to, product_id: productId } })).data);
      else setRows((await api.get("/packing-lists")).data);
    } catch (e) { setRows([]); }
  }, [tab, from, to, productId]);

  useEffect(() => { load(); }, [load]);

  const filters = tab !== "inventory" && tab !== "packing" && (
    <div className="flex flex-wrap gap-3">
      <input data-testid="report-from" type="date" className="sk-input max-w-[160px]" value={from} onChange={(e) => setFrom(e.target.value)} />
      <input data-testid="report-to" type="date" className="sk-input max-w-[160px]" value={to} onChange={(e) => setTo(e.target.value)} />
      <select data-testid="report-product" className="sk-input max-w-[220px]" value={productId} onChange={(e) => setProductId(e.target.value)}>
        <option value="">All products</option>
        {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    </div>
  );

  return (
    <div data-testid="reports-page" className="space-y-6">
      <h1 className="font-heading text-3xl font-extrabold text-[#002B5D] tracking-tight">Reports</h1>
      <div className="flex gap-1 border-b border-border flex-wrap" data-testid="report-tabs">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`report-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#EB1C27] text-[#002B5D]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.label}
          </button>
        ))}
      </div>
      {filters}

      <div className="sk-card overflow-x-auto">
        {tab === "inventory" && (
          <table className="w-full sk-table" data-testid="report-inventory">
            <thead><tr><th>Product</th><th>Code</th><th>Type</th><th className="text-right">Current Stock</th><th>UOM</th></tr></thead>
            <tbody>
              {rows?.map((p) => (
                <tr key={p.id}><td>{p.name}</td><td className="font-mono">{p.code}</td><td className="text-slate-500">{p.product_type}</td>
                  <td className="text-right font-mono font-semibold">{fmtQty(p.current_stock)}</td><td>{p.uom}</td></tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === "production" && (
          <table className="w-full sk-table" data-testid="report-production">
            <thead><tr><th>Date</th><th>Product</th><th className="text-right">Qty</th><th>Lot</th><th>MO</th><th>Start</th><th>End</th><th>Operator</th></tr></thead>
            <tbody>
              {rows?.map((r, i) => (
                <tr key={i}><td>{fmtDate(r.date)}</td><td>{r.product}</td><td className="text-right font-mono font-semibold">{fmtQty(r.qty)} {r.uom}</td>
                  <td className="font-mono">{r.lot_number}</td><td className="font-mono text-xs">{r.mo_number}</td>
                  <td className="font-mono text-xs">{r.start_time || "-"}</td><td className="font-mono text-xs">{r.end_time || "-"}</td><td>{r.operator}</td></tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === "lots" && (
          <table className="w-full sk-table" data-testid="report-lots">
            <thead><tr><th>Lot</th><th>Product</th><th className="text-right">Produced</th><th className="text-right">Current</th><th>QC</th></tr></thead>
            <tbody>
              {rows?.map((l) => (
                <tr key={l.id}><td className="font-mono font-semibold text-[#002B5D]">{l.lot_number}</td><td>{l.product_name}</td>
                  <td className="text-right font-mono">{fmtQty(l.produced_qty)}</td><td className="text-right font-mono font-semibold">{fmtQty(l.current_qty)} {l.uom}</td>
                  <td><StatusBadge status={l.qc?.status || "Pending"} /></td></tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === "movements" && (
          <table className="w-full sk-table" data-testid="report-movements">
            <thead><tr><th>Date</th><th>Product</th><th>Movement</th><th className="text-right">Qty</th><th>Reference</th></tr></thead>
            <tbody>
              {rows?.map((m) => (
                <tr key={m.id}><td className="text-slate-500">{fmtDT(m.date)}</td><td>{m.product_name}</td><td>{m.type}</td>
                  <td className={`text-right font-mono font-semibold ${m.qty < 0 ? "text-rose-600" : "text-green-700"}`}>{fmtQty(m.qty)} {m.uom}</td>
                  <td className="font-mono text-xs">{m.reference}</td></tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === "packing" && (
          <table className="w-full sk-table" data-testid="report-packing">
            <thead><tr><th>Packing List</th><th>Date</th><th>Customer</th><th>Lots</th><th className="text-right">Qty</th></tr></thead>
            <tbody>
              {rows?.map((p) => (
                <tr key={p.id}><td className="font-mono font-semibold text-[#002B5D]">{p.pl_number}</td><td>{fmtDate(p.date)}</td><td>{p.customer || "-"}</td>
                  <td className="font-mono text-xs">{(p.items || []).map((i) => i.box_number ? `${i.lot_number}/${i.box_number}` : i.lot_number).join(", ")}</td>
                  <td className="text-right font-mono font-semibold">{fmtQty(p.total_qty)}</td></tr>
              ))}
            </tbody>
          </table>
        )}
        {!rows && <div className="px-5 py-8 text-center text-slate-400 text-sm">Loading...</div>}
        {rows?.length === 0 && <div className="px-5 py-8 text-center text-slate-400 text-sm">No data</div>}
      </div>
    </div>
  );
}
